import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { fetchProjectAIConfig, updateProjectAIConfig, clearProjectConversationHistory } from '../../../utils/api';
import { useAuth } from '../../accounts/contexts/AuthContext';
import FormActions from '../../../components/FormActions';
import FormToggle from '../../../components/FormToggle';
import Loading from '../../uiStatus/components/Loading';
import { formFieldClass, formShell } from '../../../styles/formShell';

/** Function App で利用するモデルID（API と一致）と UI 表示名 */
const FUNCTION_MODEL_OPTIONS = [
    { value: 'disaster_tankyu', label: '防災シミュレーションモデル', model_type: 'disaster_prevention' },
    { value: 'disaster_bigbrother', label: '防災リサーチモデル', model_type: 'disaster_prevention' },
    { value: 'furusato', label: 'ふるさと学習モデル', model_type: 'regional_learning' },
    { value: 'map', label: 'マップモデル', model_type: 'disaster_prevention' },
];

const ProjectAIConfigForm = ({ projectId, onSuccess, onError }) => {
    const { user } = useAuth();
    const [aiConfig, setAiConfig] = useState(null);
    const [aiConfigData, setAiConfigData] = useState({
        function_model_id: 'disaster_tankyu',
        model_type: 'disaster_prevention',
        default_age: '低学年',
        default_disaster_type: '地震',
        default_tsunami: '津波あり',
        default_turn_counter: '8ターン',
        is_active: true
    });
    const [originalAiConfigData, setOriginalAiConfigData] = useState({
        function_model_id: 'disaster_tankyu',
        model_type: 'disaster_prevention',
        default_age: '低学年',
        default_disaster_type: '地震',
        default_tsunami: '津波あり',
        default_turn_counter: '8ターン',
        is_active: true
    });
    const [loading, setLoading] = useState(false);
    const [aiConfigLoading, setAiConfigLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const aiConfigFetchingRef = React.useRef(false);

    useEffect(() => {
        if (!projectId) {
            setAiConfigLoading(false);
            return;
        }

        if (aiConfigFetchingRef.current) {
            return;
        }

        const loadAiConfig = async () => {
            aiConfigFetchingRef.current = true;
            try {
                setAiConfigLoading(true);
                const config = await fetchProjectAIConfig(projectId);
                setAiConfig(config);
                const function_model_id = config.function_model_id || (config.model_type === 'regional_learning' ? 'furusato' : 'disaster_tankyu');
                const model_type = config.model_type || (function_model_id === 'furusato' ? 'regional_learning' : 'disaster_prevention');
                const configData = {
                    function_model_id,
                    model_type,
                    default_age: config.default_age || '低学年',
                    default_disaster_type: config.default_disaster_type || '地震',
                    default_tsunami: config.default_tsunami || '津波あり',
                    default_turn_counter: config.default_turn_counter || '8ターン',
                    is_active: config.is_active !== undefined ? config.is_active : true
                };
                setAiConfigData(configData);
                setOriginalAiConfigData(configData);
            } catch (err) {
                console.log('AI設定の取得に失敗しました:', err);
                setAiConfig(null);
            } finally {
                setAiConfigLoading(false);
                aiConfigFetchingRef.current = false;
            }
        };

        loadAiConfig();
    }, [projectId]);

    const handleAiConfigChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        if (errors[name]) {
            setErrors(prev => {
                const updated = { ...prev };
                delete updated[name];
                return updated;
            });
        }

        setAiConfigData(prev => {
            const updated = {
                ...prev,
                [name]: newValue
            };
            if (name === 'function_model_id') {
                const option = FUNCTION_MODEL_OPTIONS.find(o => o.value === newValue);
                updated.model_type = option ? option.model_type : (newValue === 'furusato' ? 'regional_learning' : 'disaster_prevention');
                if (newValue === 'furusato' || newValue === 'map') {
                    updated.default_tsunami = '';
                    updated.default_turn_counter = '';
                    updated.default_disaster_type = '';
                } else if (newValue === 'disaster_tankyu' || newValue === 'disaster_bigbrother') {
                    if (!updated.default_disaster_type) updated.default_disaster_type = '地震';
                    if (newValue === 'disaster_tankyu') {
                        if (updated.default_disaster_type === '大雨洪水') {
                            updated.default_tsunami = '津波なし';
                        } else if (!updated.default_tsunami) {
                            updated.default_tsunami = '津波あり';
                        }
                    } else if (!updated.default_tsunami) {
                        updated.default_tsunami = '津波あり';
                    }
                    if (!updated.default_turn_counter) updated.default_turn_counter = '8ターン';
                }
            }
            if (name === 'model_type') {
                if (newValue !== 'disaster_prevention') {
                    updated.default_tsunami = '';
                    updated.default_turn_counter = '';
                } else {
                    if (updated.function_model_id === 'disaster_tankyu' && updated.default_disaster_type === '大雨洪水') {
                        updated.default_tsunami = '津波なし';
                    } else if (!updated.default_tsunami) {
                        updated.default_tsunami = '津波あり';
                    }
                    if (!updated.default_turn_counter) updated.default_turn_counter = '8ターン';
                }
            }
            if (name === 'default_disaster_type') {
                if (updated.function_model_id === 'disaster_tankyu') {
                    if (newValue === '大雨洪水') {
                        updated.default_tsunami = '津波なし';
                    } else if (newValue === '地震' && !updated.default_tsunami) {
                        updated.default_tsunami = '津波あり';
                    }
                }
            }
            return updated;
        });
    };

    const validateForm = () => {
        const newErrors = {};
        if (aiConfigData.function_model_id === 'disaster_bigbrother' || aiConfigData.function_model_id === 'disaster_tankyu') {
            if (!aiConfigData.default_disaster_type) {
                newErrors.default_disaster_type = '災害種別を選択してください';
            }
        }
        if (aiConfigData.function_model_id === 'disaster_tankyu') {
            const hideTsunamiUi = aiConfigData.default_disaster_type === '大雨洪水';
            if (!hideTsunamiUi && !aiConfigData.default_tsunami) {
                newErrors.default_tsunami = '津波フラグを選択してください';
            }
            if (!aiConfigData.default_turn_counter) {
                newErrors.default_turn_counter = '最大ターン数を選択してください';
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const hasChanges = useCallback(() => {
        if (!aiConfig) return false;
        return (
            aiConfigData.function_model_id !== originalAiConfigData.function_model_id ||
            aiConfigData.model_type !== originalAiConfigData.model_type ||
            aiConfigData.default_age !== originalAiConfigData.default_age ||
            aiConfigData.default_disaster_type !== originalAiConfigData.default_disaster_type ||
            aiConfigData.default_tsunami !== originalAiConfigData.default_tsunami ||
            aiConfigData.default_turn_counter !== originalAiConfigData.default_turn_counter ||
            aiConfigData.is_active !== originalAiConfigData.is_active
        );
    }, [aiConfigData, originalAiConfigData, aiConfig]);

    const handleReset = () => {
        if (aiConfig) {
            const function_model_id = aiConfig.function_model_id || (aiConfig.model_type === 'regional_learning' ? 'furusato' : 'disaster_tankyu');
            const model_type = aiConfig.model_type || (function_model_id === 'furusato' ? 'regional_learning' : 'disaster_prevention');
            const resetAiConfigData = {
                function_model_id,
                model_type,
                default_age: aiConfig.default_age || '低学年',
                default_disaster_type: aiConfig.default_disaster_type || '地震',
                default_tsunami: aiConfig.default_tsunami || '津波あり',
                default_turn_counter: aiConfig.default_turn_counter || '8ターン',
                is_active: aiConfig.is_active !== undefined ? aiConfig.is_active : true
            };
            setAiConfigData(resetAiConfigData);
            setOriginalAiConfigData(resetAiConfigData);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!aiConfig) return;

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const aiPayload = { ...aiConfigData };
            if (aiPayload.function_model_id === 'furusato') {
                aiPayload.model_type = 'regional_learning';
                aiPayload.default_tsunami = null;
                aiPayload.default_turn_counter = null;
                aiPayload.default_disaster_type = null;
            } else if (aiPayload.function_model_id === 'map') {
                aiPayload.model_type = 'disaster_prevention';
                aiPayload.default_tsunami = null;
                aiPayload.default_turn_counter = null;
                aiPayload.default_disaster_type = null;
            } else {
                aiPayload.model_type = 'disaster_prevention';
                if (aiPayload.function_model_id === 'disaster_tankyu') {
                    aiPayload.default_turn_counter = '8ターン';
                    if (aiPayload.default_disaster_type === '大雨洪水') {
                        aiPayload.default_tsunami = '津波なし';
                    }
                } else {
                    aiPayload.default_tsunami = null;
                    aiPayload.default_turn_counter = null;
                }
            }
            await updateProjectAIConfig(projectId, aiPayload);

            if (user?.login_id) {
                try {
                    clearProjectConversationHistory(projectId, user.login_id);
                } catch (userErr) {
                    console.warn('会話履歴のクリアに失敗しました:', userErr);
                }
            }

            const synced = {
                ...aiConfigData,
                function_model_id: aiPayload.function_model_id,
                model_type: aiPayload.model_type,
                default_age: aiPayload.default_age,
                is_active: aiPayload.is_active
            };
            if (aiPayload.function_model_id === 'furusato' || aiPayload.function_model_id === 'map') {
                synced.default_disaster_type = '';
                synced.default_tsunami = '';
                synced.default_turn_counter = '';
            } else {
                synced.default_disaster_type = aiPayload.default_disaster_type ?? '';
                synced.default_tsunami = aiPayload.default_tsunami ?? '';
                synced.default_turn_counter = aiPayload.default_turn_counter ?? '';
            }
            setAiConfigData(synced);
            setOriginalAiConfigData(synced);
            setErrors({});

            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            console.error('AI設定更新エラー:', err);

            const errorData = err?.response?.data;
            if (errorData) {
                const backendErrors = {};
                ['function_model_id', 'default_disaster_type', 'default_tsunami', 'default_turn_counter'].forEach((key) => {
                    if (errorData[key]) {
                        backendErrors[key] = Array.isArray(errorData[key]) ? errorData[key][0] : errorData[key];
                    }
                });
                if (Object.keys(backendErrors).length > 0) {
                    setErrors(backendErrors);
                }
            }

            if (onError) {
                onError();
            }
        } finally {
            setLoading(false);
        }
    };

    if (aiConfigLoading) {
        return <Loading isLoading message="AI設定を読み込み中..." />;
    }

    if (!aiConfig) {
        return <p className={formShell.hint}>AI設定が見つかりませんでした。</p>;
    }

    return (
        <form onSubmit={handleSubmit} className={formShell.form}>
            <div className={`${formShell.grid2} items-start`}>
                <div className={formShell.togglePanel}>
                    <FormToggle
                        id="ai_is_active"
                        name="is_active"
                        checked={aiConfigData.is_active}
                        onChange={handleAiConfigChange}
                        label="AI機能を有効にする"
                    />
                </div>
            </div>

            <div className={formShell.gridAi}>
                <div className={formShell.group}>
                    <label htmlFor="ai_function_model_id" className={formShell.label}>
                        AIモデル
                    </label>
                    <select
                        id="ai_function_model_id"
                        name="function_model_id"
                        value={aiConfigData.function_model_id || 'disaster_tankyu'}
                        onChange={handleAiConfigChange}
                        className={formFieldClass(false, 'select')}
                    >
                        {FUNCTION_MODEL_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={formShell.group}>
                    <label htmlFor="ai_default_age" className={formShell.label}>
                        デフォルト年齢
                    </label>
                    <select
                        id="ai_default_age"
                        name="default_age"
                        value={aiConfigData.default_age}
                        onChange={handleAiConfigChange}
                        className={formFieldClass(false, 'select')}
                    >
                        <option value="低学年">低学年</option>
                        <option value="中学年">中学年</option>
                        <option value="高学年">高学年</option>
                        <option value="中学生">中学生</option>
                        <option value="高校生">高校生</option>
                        <option value="大人">大人</option>
                    </select>
                </div>

                {(aiConfigData.function_model_id === 'disaster_bigbrother' || aiConfigData.function_model_id === 'disaster_tankyu') && (
                    <div className={formShell.group}>
                        <label htmlFor="ai_default_disaster_type" className={formShell.label}>
                            デフォルト災害種別
                        </label>
                        <select
                            id="ai_default_disaster_type"
                            name="default_disaster_type"
                            value={aiConfigData.default_disaster_type || ''}
                            onChange={handleAiConfigChange}
                            className={formFieldClass(Boolean(errors.default_disaster_type), 'select')}
                        >
                            <option value="地震">地震</option>
                            <option value="大雨洪水">大雨洪水</option>
                        </select>
                        {errors.default_disaster_type && (
                            <p className={formShell.error}>{errors.default_disaster_type}</p>
                        )}
                    </div>
                )}

                {aiConfigData.function_model_id === 'disaster_tankyu' && (
                    <>
                        {aiConfigData.default_disaster_type !== '大雨洪水' && (
                            <div className={formShell.group}>
                                <label htmlFor="ai_default_tsunami" className={formShell.label}>
                                    デフォルト津波フラグ
                                </label>
                                <select
                                    id="ai_default_tsunami"
                                    name="default_tsunami"
                                    value={aiConfigData.default_tsunami}
                                    onChange={handleAiConfigChange}
                                    className={formFieldClass(Boolean(errors.default_tsunami), 'select')}
                                >
                                    <option value="津波あり">津波あり</option>
                                    <option value="津波なし">津波なし</option>
                                </select>
                                {errors.default_tsunami && (
                                    <p className={formShell.error}>{errors.default_tsunami}</p>
                                )}
                            </div>
                        )}

                        <div className={formShell.group}>
                            <label htmlFor="ai_default_turn_counter" className={formShell.label}>
                                デフォルト最大ターン数
                            </label>
                            <div id="ai_default_turn_counter" className={formShell.readonlyValue}>
                                8ターン（固定）
                            </div>
                        </div>
                    </>
                )}
            </div>

            <FormActions
                hasChanges={hasChanges()}
                loading={loading}
                onReset={handleReset}
                onSave={handleSubmit}
                submitType="submit"
            />
        </form>
    );
};

ProjectAIConfigForm.propTypes = {
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onSuccess: PropTypes.func,
    onError: PropTypes.func
};

export default ProjectAIConfigForm;
